import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Breadcrumb, Loader, Stepper } from 'components';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import { InfoIcon } from 'static';
import {
  cancelSubscription,
  getCurrentUserSubscription,
} from 'store/actionCreators/subscriptions';
import {
  selectCurrentUserSubscription,
  selectIsSubscriptionLoading,
} from 'store/selectors/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';
import { formatSubscriptionDate } from 'utils/dates';
import { getBillingPortalUrl } from 'utils/subscriptionBilling';

import {
  AccountAvatar,
  AccountCard,
  AccountLeft,
  AccountMeta,
  AccountName,
  BackLink,
  BtnDanger,
  BtnPrimary,
  BtnSecondary,
  ButtonRow,
  CheckboxRow,
  ConfirmLabel,
  ConfirmRow,
  ConfirmSummary,
  ConfirmValue,
  ConfirmWarn,
  EntOfferBox,
  EntOfferCta,
  EntOfferDesc,
  EntOfferTitle,
  ErrorNote,
  FollowUpLabel,
  FollowUpTextarea,
  FollowUpWrap,
  InfoBox,
  InitialLoaderWrap,
  LossBox,
  LossHeading,
  LossItem,
  LossList,
  OfferBox,
  OfferCta,
  OfferDesc,
  OfferIcon,
  OfferTitle,
  OrDivider,
  PageWrap,
  PlanPill,
  ReasonCard,
  ReasonDot,
  ReasonGrid,
  ReasonSub,
  ReasonText,
  RetentionCard,
  RetentionCta,
  RetentionDesc,
  RetentionGrid,
  RetentionIcon,
  RetentionTitle,
  Spinner,
  StepDesc,
  StepHeader,
  StepTitle,
  SuccessActions,
  SuccessCard,
  SuccessIconCircle,
  SuccessSub,
  SuccessTitle,
  SuccessWrap,
  UndoBanner,
  UndoBtn,
  UndoSub,
  UndoText,
} from './styled';
import { OutsideAppPageContentWrapper } from 'components/Shared/styled';

type Scenario = 'trial' | 'pro' | 'premium' | 'enterprise';

type ReasonId =
  | 'price'
  | 'missing'
  | 'difficult'
  | 'alternative'
  | 'pause'
  | 'dissolve'
  | 'other';

const REASONS_ALL: ReasonId[] = [
  'price',
  'missing',
  'difficult',
  'alternative',
  'pause',
  'dissolve',
  'other',
];

const BOOK_CALL_URL = 'https://cal.com/gumbapp/30min?overlayCalendar=true';
const HELP_CENTER_URLS = {
  default: 'https://gumb.app/en/support/',
  de: 'https://gumb.app/de/support/',
} as const;
const FEEDBACK_URLS = {
  default: 'https://gumb.app/en/contact-us/',
  de: 'https://gumb.app/de/contact-us/',
} as const;

const openExternalUrlInNewTab = (url: string) => {
  if (typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const getLocaleUrl = (
  language: string | undefined,
  urls: { default: string; de: string },
) => (language?.toLowerCase().startsWith('de') ? urls.de : urls.default);

/**
 * Resolve the cancellation scenario from the user's subscription.
 *
 * Precedence:
 * 1. Free Trial - `planId === TRIAL` (or trial status)
 * 2. Enterprise - only when EXPLICITLY identified
 *    (`enterpriseInvoiceRequest`, `enterpriseInvoiceRequestId`,
 *     or "enterprise" in plan label).
 * 3. Premium - premium label in plan name/productName.
 * 4. Pro - remaining paid fallback.
 *
 * Note: `stripeCollectionMethod === 'send_invoice'` is a billing method
 * only - it must NOT classify a subscription as Enterprise on its own.
 */
const resolveScenario = (
  sub: UserSubscription | null | undefined,
): Scenario => {
  if (!sub) return 'pro';

  if (
    sub.planId === SubscriptionStatus.TRIAL ||
    sub.status === SubscriptionStatus.TRIAL
  ) {
    return 'trial';
  }

  const label = `${sub.subscriptionPlan?.productName ?? ''} ${
    sub.subscriptionPlan?.name ?? ''
  }`.toLowerCase();

  const hasEnterpriseSignal =
    /enterprise/.test(label) ||
    Boolean(sub.enterpriseInvoiceRequest) ||
    Boolean(sub.enterpriseInvoiceRequestId);

  if (hasEnterpriseSignal) return 'enterprise';

  if (/premium/.test(label)) return 'premium';
  if (/\bpro\b/.test(label)) return 'pro';

  // Last-resort heuristic when label isn't clear: multi-community plans
  // are treated as premium-like, otherwise pro.
  if ((sub.maxCommunities ?? 0) > 1) return 'premium';
  return 'pro';
};

const initialsFromName = (name: string | undefined | null) => {
  if (!name) return 'GB';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'GB';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const CancelSubscriptionPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const subscription = useSelector(selectCurrentUserSubscription) as
    | UserSubscription
    | null
    | undefined;
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const isSubscriptionLoading = useSelector(selectIsSubscriptionLoading);

  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const changePlanPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;

  /** Snapshot the scenario at mount so the UI doesn't change after API success. */
  const [scenario] = useState<Scenario>(() => resolveScenario(subscription));

  const [step, setStep] = useState<1 | 2 | 3 | 4>(() => {
    if (!subscription) return 1;
    if (
      subscription.status === SubscriptionStatus.CANCELLED &&
      subscription.subscriptionId !== SubscriptionStatus.MIGRATED
    ) {
      return 4;
    }
    if (
      subscription.stripeSubscriptionStatus === 'active' &&
      subscription.stripeCancelAtPeriodEnd === true
    ) {
      return 4;
    }
    if (
      subscription.stripeSubscriptionStatus &&
      ['canceled', 'unpaid', 'incomplete_expired'].includes(
        subscription.stripeSubscriptionStatus,
      )
    ) {
      return 4;
    }
    return 1;
  });
  const [isResolvingInitialStep, setIsResolvingInitialStep] = useState(
    () => !subscription,
  );
  const [reason, setReason] = useState<ReasonId | null>(null);
  const [reasonNote, setReasonNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cancelledAt, setCancelledAt] = useState<number | null>(null);

  // Refresh subscription data when entering the page so dates are accurate.
  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [dispatch]);

  // Detect cancellation API completion: while we're submitting, the saga
  // updates the store with the new subscription. Move to Done only after
  // the store reflects a cancelled / non-renewing state.
  const isStoreCancelled = useMemo(() => {
    if (!subscription) return false;
    if (
      subscription.status === SubscriptionStatus.CANCELLED &&
      subscription.subscriptionId !== SubscriptionStatus.MIGRATED
    ) {
      return true;
    }
    if (
      subscription.stripeSubscriptionStatus === 'active' &&
      subscription.stripeCancelAtPeriodEnd === true
    ) {
      return true;
    }
    if (
      subscription.stripeSubscriptionStatus &&
      ['canceled', 'unpaid', 'incomplete_expired'].includes(
        subscription.stripeSubscriptionStatus,
      )
    ) {
      return true;
    }
    return false;
  }, [subscription]);

  useEffect(() => {
    if (!isResolvingInitialStep) return;
    if (isSubscriptionLoading) return;
    if (isStoreCancelled) {
      setStep(4);
    }
    setIsResolvingInitialStep(false);
  }, [isResolvingInitialStep, isSubscriptionLoading, isStoreCancelled]);

  useEffect(() => {
    if (submitting && isStoreCancelled) {
      setSubmitting(false);
      setSubmitError(null);
      setCancelledAt(Date.now());
      setStep(4);
    }
  }, [submitting, isStoreCancelled]);

  // If the saga finished but the store didn't move to a cancelled state
  // (failure path), the loader flips back to false and we clear submitting.
  useEffect(() => {
    if (submitting && !isSubscriptionLoading && !isStoreCancelled) {
      // Slight delay so we don't race the success branch above.
      const id = window.setTimeout(() => {
        setSubmitting((prev) => {
          if (prev && !isStoreCancelled) {
            setSubmitError(t('cancelSubscription.errors.generic'));
            return false;
          }
          return prev;
        });
      }, 300);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [submitting, isSubscriptionLoading, isStoreCancelled, t]);

  const accountName = useMemo(() => {
    const owned = communities.find(
      (c) => c?.communityOwner?.userId === profile?.userId,
    );
    if (owned?.name) return owned.name;
    const composed = `${profile?.firstName ?? ''} ${
      profile?.lastName ?? ''
    }`.trim();
    if (composed) return composed;
    return profile?.profileName || profile?.email || 'Gumb';
  }, [communities, profile]);

  const accountInitials = useMemo(
    () => initialsFromName(accountName),
    [accountName],
  );

  const planDisplayName = useMemo(() => {
    if (scenario === 'trial') return t('cancelSubscription.plan.trial');
    return (
      subscription?.subscriptionPlan?.productName ||
      subscription?.subscriptionPlan?.name ||
      t(`cancelSubscription.plan.${scenario}`)
    );
  }, [scenario, subscription, t]);

  const accessEndIso = useMemo(() => {
    if (!subscription) return null;
    return (
      subscription.stripeCancelAt ||
      subscription.stripeCurrentPeriodEnd ||
      subscription.stripeEndedAt ||
      subscription.endDate ||
      null
    );
  }, [subscription]);

  const accessEndDate = useMemo(
    () => formatSubscriptionDate(accessEndIso, i18n.language),
    [accessEndIso, i18n.language],
  );
  console.log('subscription', subscription);
  const billingPortalUrl = useMemo(
    () => getBillingPortalUrl(subscription ?? undefined),
    [subscription],
  );

  const teamsCount = useMemo(() => {
    if (!profile?.userId) return 0;
    return communities.filter(
      (c) => c?.communityOwner?.userId === profile.userId,
    ).length;
  }, [communities, profile?.userId]);

  const memberCount = subscription?.info?.users ?? 0;
  const maxCommunities = subscription?.maxCommunities ?? 0;
  const isPremiumTwoPlan = scenario === 'premium' && maxCommunities === 2;
  const isPremiumHigherTier = scenario === 'premium' && maxCommunities > 2;
  const isYearly = subscription?.frequency === SubscriptionFrequency.YEAR;
  const isInvoiceBilled =
    subscription?.stripeCollectionMethod === 'send_invoice';

  const enterpriseAccountManagerName =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_NAME || '';
  const enterpriseAccountManagerEmail =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_EMAIL || '';

  const planPillTone: 'trial' | 'pro' | 'premium' | 'enterprise' = scenario;
  const planPillLabel = useMemo(() => {
    if (scenario === 'trial') return t('cancelSubscription.pill.trial');
    if (scenario === 'pro') return t('cancelSubscription.pill.pro');
    if (scenario === 'premium') return t('cancelSubscription.pill.premium');
    return t('cancelSubscription.pill.enterprise');
  }, [scenario, t]);

  const accountMetaLine = useMemo(() => {
    if (scenario === 'trial') {
      return t('cancelSubscription.meta.trial', { date: accessEndDate });
    }
    if (scenario === 'enterprise') {
      return t('cancelSubscription.meta.enterprise', {
        teams: teamsCount,
        members: memberCount,
      });
    }
    return t('cancelSubscription.meta.paid', {
      plan: planDisplayName,
      teams: teamsCount,
      members: memberCount,
      date: accessEndDate,
    });
  }, [scenario, accessEndDate, teamsCount, memberCount, planDisplayName, t]);

  const goStep = useCallback(
    (next: 1 | 2 | 3 | 4) => {
      setStep(next);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [setStep],
  );

  const handleConfirmCancel = useCallback(() => {
    if (!subscription?.subscriptionId) {
      setSubmitError(t('cancelSubscription.errors.noSubscription'));
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    dispatch(cancelSubscription.trigger(subscription.subscriptionId));
  }, [dispatch, subscription?.subscriptionId, t]);

  const goBackToSubscription = useCallback(() => {
    navigate(subscriptionPath);
  }, [navigate, subscriptionPath]);

  const handleBookCall = useCallback(() => {
    openExternalUrlInNewTab(BOOK_CALL_URL);
  }, []);

  const handleOpenHelpCenter = useCallback(() => {
    openExternalUrlInNewTab(getLocaleUrl(i18n.language, HELP_CENTER_URLS));
  }, [i18n.language]);

  const handleSubmitFeedback = useCallback(() => {
    openExternalUrlInNewTab(getLocaleUrl(i18n.language, FEEDBACK_URLS));
  }, [i18n.language]);
  const handleUndoCancellation = useCallback(() => {
    if (billingPortalUrl) {
      openExternalUrlInNewTab(billingPortalUrl);
      return;
    }

    goBackToSubscription();
  }, [billingPortalUrl, goBackToSubscription]);

  const reasonOptions: { id: ReasonId; show: boolean }[] = useMemo(
    () =>
      REASONS_ALL.map((id) => ({
        id,
        // The price reason is hidden for trial users (they aren't being charged).
        show: !(scenario === 'trial' && id === 'price'),
      })),
    [scenario],
  );

  const reasonFollowupLabel = useMemo(() => {
    if (!reason) return t('cancelSubscription.followup.default');
    return t(`cancelSubscription.followup.${reason}`);
  }, [reason, t]);

  /* ------------------------------------------------------------------ */
  /* Renderers                                                            */
  /* ------------------------------------------------------------------ */

  const renderProgress = () => {
    const stepDefinitions: { n: 1 | 2 | 3 | 4; label: string }[] = [
      { n: 1, label: t('cancelSubscription.progress.reason') },
      { n: 2, label: t('cancelSubscription.progress.alternatives') },
      { n: 3, label: t('cancelSubscription.progress.confirm') },
      { n: 4, label: t('cancelSubscription.progress.done') },
    ];
    /** Cancellation is final; once on the success screen the stepper is read-only. */
    const isTerminal = step === 4;
    return (
      <Stepper
        steps={stepDefinitions.map((s) => {
          const isDone = step > s.n || (step === 4 && s.n === 4);
          const isActive = step === s.n;
          return {
            label: s.label,
            status: isDone ? 'done' : isActive ? 'active' : 'pending',
            onClick: !isTerminal && isDone ? () => goStep(s.n) : undefined,
          };
        })}
      />
    );
  };

  const renderAccountCard = () => (
    <AccountCard>
      <AccountLeft>
        <AccountAvatar aria-hidden>{accountInitials}</AccountAvatar>
        <div style={{ minWidth: 0 }}>
          <AccountName>{accountName}</AccountName>
          <AccountMeta>{accountMetaLine}</AccountMeta>
        </div>
      </AccountLeft>
      <PlanPill $tone={planPillTone}>{planPillLabel}</PlanPill>
    </AccountCard>
  );

  const renderStep1 = () => (
    <>
      <StepHeader>
        <StepTitle>
          {t(
            scenario === 'trial'
              ? 'cancelSubscription.step1.titleTrial'
              : 'cancelSubscription.step1.title',
          )}
        </StepTitle>
        <StepDesc>
          {t(
            scenario === 'trial'
              ? 'cancelSubscription.step1.descTrial'
              : scenario === 'enterprise'
              ? 'cancelSubscription.step1.descEnterprise'
              : 'cancelSubscription.step1.desc',
          )}
        </StepDesc>
      </StepHeader>

      {renderAccountCard()}

      <ReasonGrid>
        {reasonOptions
          .filter((r) => r.show)
          .map((r) => (
            <ReasonCard
              key={r.id}
              type="button"
              $selected={reason === r.id}
              onClick={() => setReason(r.id)}
            >
              <ReasonDot $selected={reason === r.id} />
              <div>
                <ReasonText>
                  {t(`cancelSubscription.reasons.${r.id}.title`)}
                </ReasonText>
                <ReasonSub>
                  {t(`cancelSubscription.reasons.${r.id}.sub`)}
                </ReasonSub>
              </div>
            </ReasonCard>
          ))}
      </ReasonGrid>

      {reason ? (
        <FollowUpWrap>
          <FollowUpLabel htmlFor="cancel-reason-note">
            {reasonFollowupLabel}
          </FollowUpLabel>
          <FollowUpTextarea
            id="cancel-reason-note"
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            placeholder={t('cancelSubscription.followup.placeholder')}
          />
        </FollowUpWrap>
      ) : null}

      <ButtonRow>
        <BackLink type="button" onClick={goBackToSubscription}>
          {t('cancelSubscription.actions.backToSubscription')}
        </BackLink>
        <BtnPrimary type="button" disabled={!reason} onClick={() => goStep(2)}>
          {t('cancelSubscription.actions.continue')}
        </BtnPrimary>
      </ButtonRow>
    </>
  );

  const renderRetentionTrial = () => (
    <>
      <OfferBox>
        <OfferIcon>⏱</OfferIcon>
        <div>
          <OfferTitle>
            {t('cancelSubscription.retention.trial.offerTitle')}
          </OfferTitle>
          <OfferDesc>
            {t('cancelSubscription.retention.trial.offerDesc', {
              date: accessEndDate,
            })}
          </OfferDesc>
          <OfferCta type="button" onClick={goBackToSubscription}>
            {t('cancelSubscription.retention.trial.offerCta')}
          </OfferCta>
        </div>
      </OfferBox>
      <RetentionGrid>
        <RetentionCard>
          <RetentionIcon>📋</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.trial.tutorialTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.trial.tutorialDesc')}
          </RetentionDesc>
          <RetentionCta type="button">
            {t('cancelSubscription.retention.trial.tutorialCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard $featured>
          <RetentionIcon>💬</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.shared.talkTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.trial.talkDesc')}
          </RetentionDesc>
          <RetentionCta type="button" $primary onClick={handleBookCall}>
            {t('cancelSubscription.retention.shared.talkCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>🗂</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.trial.featureTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.trial.featureDesc')}
          </RetentionDesc>
          <RetentionCta type="button">
            {t('cancelSubscription.retention.trial.featureCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>📖</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.shared.helpTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.trial.helpDesc')}
          </RetentionDesc>
          <RetentionCta type="button" onClick={handleOpenHelpCenter}>
            {t('cancelSubscription.retention.shared.helpCta')}
          </RetentionCta>
        </RetentionCard>
      </RetentionGrid>
    </>
  );

  const renderRetentionPro = () => (
    <>
      <RetentionGrid>
        <RetentionCard $featured>
          <RetentionIcon>💬</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.shared.talkTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.pro.talkDesc')}
          </RetentionDesc>
          <RetentionCta type="button" $primary onClick={handleBookCall}>
            {t('cancelSubscription.retention.shared.talkCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>⏸</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.pro.pauseTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.pro.pauseDesc')}
          </RetentionDesc>
          <RetentionCta type="button">
            {t('cancelSubscription.retention.pro.pauseCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>🗂</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.pro.missingTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.pro.missingDesc')}
          </RetentionDesc>
          <RetentionCta type="button" onClick={handleSubmitFeedback}>
            {t('cancelSubscription.retention.pro.missingCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>📖</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.pro.helpTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.pro.helpDesc')}
          </RetentionDesc>
          <RetentionCta type="button" onClick={handleOpenHelpCenter}>
            {t('cancelSubscription.retention.shared.helpCta')}
          </RetentionCta>
        </RetentionCard>
      </RetentionGrid>
    </>
  );

  const renderRetentionPremium = () => (
    <>
      {(isPremiumTwoPlan || isPremiumHigherTier) && (
        <OfferBox>
          <OfferIcon>🔽</OfferIcon>
          <div>
            <OfferTitle>
              {t(
                isPremiumTwoPlan
                  ? 'cancelSubscription.retention.premium.offerTitle'
                  : 'cancelSubscription.retention.premium.offerTitleGeneric',
              )}
            </OfferTitle>
            <OfferDesc>
              {t(
                isPremiumTwoPlan
                  ? 'cancelSubscription.retention.premium.offerDesc'
                  : 'cancelSubscription.retention.premium.offerDescGeneric',
              )}
            </OfferDesc>
            <OfferCta type="button" onClick={() => navigate(changePlanPath)}>
              {t('cancelSubscription.retention.premium.offerCta')}
            </OfferCta>
          </div>
        </OfferBox>
      )}
      <RetentionGrid>
        <RetentionCard $featured>
          <RetentionIcon>💬</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.shared.talkTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.premium.talkDesc')}
          </RetentionDesc>
          <RetentionCta type="button" $primary onClick={handleBookCall}>
            {t('cancelSubscription.retention.shared.talkCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>⏸</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.premium.pauseTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.premium.pauseDesc')}
          </RetentionDesc>
          <RetentionCta type="button">
            {t('cancelSubscription.retention.premium.pauseCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>🗂</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.premium.missingTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.premium.missingDesc')}
          </RetentionDesc>
          <RetentionCta type="button" onClick={handleSubmitFeedback}>
            {t('cancelSubscription.retention.premium.missingCta')}
          </RetentionCta>
        </RetentionCard>
        <RetentionCard>
          <RetentionIcon>📊</RetentionIcon>
          <RetentionTitle>
            {t('cancelSubscription.retention.premium.usageTitle')}
          </RetentionTitle>
          <RetentionDesc>
            {t('cancelSubscription.retention.premium.usageDesc')}
          </RetentionDesc>
          <RetentionCta
            as={Link}
            type="button"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore styled `as` widening
            to={changePlanPath}
          >
            {t('cancelSubscription.retention.premium.usageCta')}
          </RetentionCta>
        </RetentionCard>
      </RetentionGrid>
    </>
  );

  const renderRetentionEnterprise = () => {
    const managerLine =
      enterpriseAccountManagerName && enterpriseAccountManagerEmail
        ? `${enterpriseAccountManagerName} (${enterpriseAccountManagerEmail})`
        : enterpriseAccountManagerName ||
          enterpriseAccountManagerEmail ||
          t('cancelSubscription.retention.enterprise.managerFallback');
    return (
      <>
        <EntOfferBox>
          <OfferIcon>🤝🏻</OfferIcon>
          <div>
            <EntOfferTitle>
              {t('cancelSubscription.retention.enterprise.offerTitle')}
            </EntOfferTitle>
            <EntOfferDesc>
              {t('cancelSubscription.retention.enterprise.offerDesc', {
                manager: managerLine,
              })}
            </EntOfferDesc>
            {enterpriseAccountManagerEmail ? (
              <EntOfferCta
                as="a"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore styled `as` widening
                href={`mailto:${enterpriseAccountManagerEmail}`}
              >
                {t('cancelSubscription.retention.enterprise.offerCta')}
              </EntOfferCta>
            ) : (
              <EntOfferCta type="button" onClick={goBackToSubscription}>
                {t('cancelSubscription.retention.enterprise.offerCta')}
              </EntOfferCta>
            )}
          </div>
        </EntOfferBox>
        <RetentionGrid>
          <RetentionCard $featured>
            <RetentionIcon>🔽</RetentionIcon>
            <RetentionTitle>
              {t('cancelSubscription.retention.enterprise.downgradeTitle')}
            </RetentionTitle>
            <RetentionDesc>
              {t('cancelSubscription.retention.enterprise.downgradeDesc')}
            </RetentionDesc>
            <RetentionCta
              as={Link}
              type="button"
              $primary
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore styled `as` widening
              to={`${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`}
            >
              {t('cancelSubscription.retention.enterprise.downgradeCta')}
            </RetentionCta>
          </RetentionCard>
          <RetentionCard>
            <RetentionIcon>🔄</RetentionIcon>
            <RetentionTitle>
              {t('cancelSubscription.retention.enterprise.renegotiateTitle')}
            </RetentionTitle>
            <RetentionDesc>
              {t('cancelSubscription.retention.enterprise.renegotiateDesc')}
            </RetentionDesc>
            <RetentionCta type="button" onClick={handleBookCall}>
              {t('cancelSubscription.retention.shared.talkCta')}
            </RetentionCta>
          </RetentionCard>
          <RetentionCard>
            <RetentionIcon>📦</RetentionIcon>
            <RetentionTitle>
              {t('cancelSubscription.retention.enterprise.exportTitle')}
            </RetentionTitle>
            <RetentionDesc>
              {t('cancelSubscription.retention.enterprise.exportDesc')}
            </RetentionDesc>
            <RetentionCta type="button">
              {t('cancelSubscription.retention.enterprise.exportCta')}
            </RetentionCta>
          </RetentionCard>
          <RetentionCard>
            <RetentionIcon>⏸</RetentionIcon>
            <RetentionTitle>
              {t('cancelSubscription.retention.enterprise.holdTitle')}
            </RetentionTitle>
            <RetentionDesc>
              {t('cancelSubscription.retention.enterprise.holdDesc')}
            </RetentionDesc>
            <RetentionCta type="button">
              {t('cancelSubscription.retention.enterprise.holdCta')}
            </RetentionCta>
          </RetentionCard>
        </RetentionGrid>
      </>
    );
  };

  const renderStep2 = () => (
    <>
      <StepHeader>
        <StepTitle>
          {t(
            scenario === 'trial'
              ? 'cancelSubscription.step2.titleTrial'
              : scenario === 'enterprise'
              ? 'cancelSubscription.step2.titleEnterprise'
              : 'cancelSubscription.step2.title',
          )}
        </StepTitle>
        <StepDesc>
          {t(
            scenario === 'trial'
              ? 'cancelSubscription.step2.descTrial'
              : scenario === 'enterprise'
              ? 'cancelSubscription.step2.descEnterprise'
              : 'cancelSubscription.step2.desc',
          )}
        </StepDesc>
      </StepHeader>

      {scenario === 'trial' && renderRetentionTrial()}
      {scenario === 'pro' && renderRetentionPro()}
      {scenario === 'premium' && renderRetentionPremium()}
      {scenario === 'enterprise' && renderRetentionEnterprise()}

      <OrDivider>
        <span>{t('cancelSubscription.actions.or')}</span>
      </OrDivider>

      <ButtonRow>
        <BackLink type="button" onClick={() => goStep(1)}>
          {t('cancelSubscription.actions.back')}
        </BackLink>
        <BtnDanger type="button" onClick={() => goStep(3)}>
          {t('cancelSubscription.actions.continueCancel')}
        </BtnDanger>
      </ButtonRow>
    </>
  );

  const renderConfirmTrial = () => (
    <>
      <ConfirmSummary>
        <ConfirmRow>
          <ConfirmLabel>
            {t('cancelSubscription.confirm.row.plan')}
          </ConfirmLabel>
          <ConfirmValue>{planDisplayName}</ConfirmValue>
        </ConfirmRow>
        <ConfirmRow>
          <ConfirmLabel>
            {t('cancelSubscription.confirm.row.trialEnds')}
          </ConfirmLabel>
          <ConfirmValue $tone="red">
            {t('cancelSubscription.confirm.value.immediately')}
          </ConfirmValue>
        </ConfirmRow>
        <ConfirmRow>
          <ConfirmLabel>
            {t('cancelSubscription.confirm.row.dataRetention')}
          </ConfirmLabel>
          <ConfirmValue>
            {t('cancelSubscription.confirm.value.retention30')}
          </ConfirmValue>
        </ConfirmRow>
        <ConfirmRow>
          <ConfirmLabel>
            {t('cancelSubscription.confirm.row.refund')}
          </ConfirmLabel>
          <ConfirmValue $tone="green">
            {t('cancelSubscription.confirm.value.refundTrial')}
          </ConfirmValue>
        </ConfirmRow>
      </ConfirmSummary>
      <LossBox>
        <LossHeading>
          ⚠ {t('cancelSubscription.loss.headingImmediate')}
        </LossHeading>
        <LossList>
          {[
            'communities',
            'calendar',
            'members',
            'chat',
            'storage',
            'schedulers',
          ].map((k) => (
            <LossItem key={k}>
              <span>✗</span>
              {t(`cancelSubscription.loss.items.${k}`)}
            </LossItem>
          ))}
        </LossList>
      </LossBox>
      {submitError ? <ErrorNote>{submitError}</ErrorNote> : null}
      <CheckboxRow>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span
          dangerouslySetInnerHTML={{
            __html: t('cancelSubscription.confirm.ackTrial'),
          }}
        />
      </CheckboxRow>
      <ConfirmWarn>
        <strong>{t('cancelSubscription.confirm.warnTitle')}</strong>
        {t('cancelSubscription.confirm.warnTrial')}
      </ConfirmWarn>
      <ButtonRow>
        <BackLink type="button" onClick={() => goStep(2)} disabled={submitting}>
          {t('cancelSubscription.actions.back')}
        </BackLink>
        <BtnDanger
          type="button"
          disabled={!acknowledged || submitting}
          onClick={handleConfirmCancel}
        >
          {submitting ? <Spinner /> : null}
          {t('cancelSubscription.actions.cancelTrial')}
        </BtnDanger>
      </ButtonRow>
    </>
  );

  const renderConfirmPaid = () => {
    const planLine = `${planDisplayName} · ${
      isYearly
        ? t('cancelSubscription.confirm.value.frequencyYear')
        : t('cancelSubscription.confirm.value.frequencyMonth')
    }`;
    return (
      <>
        <ConfirmSummary>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.plan')}
            </ConfirmLabel>
            <ConfirmValue>{planLine}</ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.accessUntil')}
            </ConfirmLabel>
            <ConfirmValue>{accessEndDate}</ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.nextCharge')}
            </ConfirmLabel>
            <ConfirmValue $tone="green">
              {t('cancelSubscription.confirm.value.noNextCharge')}
            </ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.refund')}
            </ConfirmLabel>
            <ConfirmValue>
              {t('cancelSubscription.confirm.value.noRefund')}
            </ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.dataRetention')}
            </ConfirmLabel>
            <ConfirmValue>
              {t('cancelSubscription.confirm.value.retentionAfterAccess')}
            </ConfirmValue>
          </ConfirmRow>
        </ConfirmSummary>
        <LossBox>
          <LossHeading>
            ⚠{' '}
            {t('cancelSubscription.loss.headingFromDate', {
              date: accessEndDate,
            })}
          </LossHeading>
          <LossList>
            {[
              'communities',
              'calendar',
              'schedulers',
              'storage',
              'members',
              'analytics',
            ].map((k) => (
              <LossItem key={k}>
                <span>✗</span>
                {t(`cancelSubscription.loss.items.${k}`)}
              </LossItem>
            ))}
          </LossList>
        </LossBox>
        <InfoBox>
          <InfoIcon />
          <span
            dangerouslySetInnerHTML={{
              __html: t('cancelSubscription.confirm.infoStaysActive', {
                plan: planDisplayName,
                date: accessEndDate,
              }),
            }}
          />
        </InfoBox>
        {submitError ? <ErrorNote>{submitError}</ErrorNote> : null}
        <CheckboxRow>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span
            dangerouslySetInnerHTML={{
              __html: t('cancelSubscription.confirm.ackPaid', {
                date: accessEndDate,
              }),
            }}
          />
        </CheckboxRow>
        <ButtonRow>
          <BackLink
            type="button"
            onClick={() => goStep(2)}
            disabled={submitting}
          >
            {t('cancelSubscription.actions.back')}
          </BackLink>
          <BtnDanger
            type="button"
            disabled={!acknowledged || submitting}
            onClick={handleConfirmCancel}
          >
            {submitting ? <Spinner /> : null}
            {t('cancelSubscription.actions.confirmCancel')}
          </BtnDanger>
        </ButtonRow>
      </>
    );
  };

  const renderConfirmEnterprise = () => {
    const managerLine =
      enterpriseAccountManagerName && enterpriseAccountManagerEmail
        ? `${enterpriseAccountManagerName} (${enterpriseAccountManagerEmail})`
        : enterpriseAccountManagerName ||
          enterpriseAccountManagerEmail ||
          t('cancelSubscription.retention.enterprise.managerFallback');
    return (
      <>
        <ConfirmSummary>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.plan')}
            </ConfirmLabel>
            <ConfirmValue>{planDisplayName}</ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.contractEnd')}
            </ConfirmLabel>
            <ConfirmValue>
              {accessEndIso
                ? accessEndDate
                : t('cancelSubscription.confirm.value.contractTerms')}
            </ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.invoicing')}
            </ConfirmLabel>
            <ConfirmValue>
              {t(
                isInvoiceBilled
                  ? 'cancelSubscription.confirm.value.invoicingFinal'
                  : 'cancelSubscription.confirm.value.invoicingPerContract',
              )}
            </ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.dataExport')}
            </ConfirmLabel>
            <ConfirmValue>
              {t('cancelSubscription.confirm.value.exportAssisted')}
            </ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>
              {t('cancelSubscription.confirm.row.dataRetention')}
            </ConfirmLabel>
            <ConfirmValue>
              {t('cancelSubscription.confirm.value.retentionContract')}
            </ConfirmValue>
          </ConfirmRow>
        </ConfirmSummary>
        <LossBox>
          <LossHeading>
            ⚠ {t('cancelSubscription.loss.headingAfter')}
          </LossHeading>
          <LossList>
            {[
              'unlimitedCommunities',
              'unlimitedMembers',
              'manager',
              'apiExport',
              'invoicingSla',
              'prioritySupport',
            ].map((k) => (
              <LossItem key={k}>
                <span>✗</span>
                {t(`cancelSubscription.loss.items.${k}`)}
              </LossItem>
            ))}
          </LossList>
        </LossBox>
        <ConfirmWarn>
          <strong>{t('cancelSubscription.confirm.enterpriseWarnTitle')}</strong>
          {t('cancelSubscription.confirm.enterpriseWarnBody', {
            manager: managerLine,
          })}
        </ConfirmWarn>
        {submitError ? <ErrorNote>{submitError}</ErrorNote> : null}
        <CheckboxRow>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span
            dangerouslySetInnerHTML={{
              __html: t('cancelSubscription.confirm.ackEnterprise'),
            }}
          />
        </CheckboxRow>
        <ButtonRow>
          <BackLink
            type="button"
            onClick={() => goStep(2)}
            disabled={submitting}
          >
            {t('cancelSubscription.actions.back')}
          </BackLink>
          <BtnDanger
            type="button"
            disabled={!acknowledged || submitting}
            onClick={handleConfirmCancel}
          >
            {submitting ? <Spinner /> : null}
            {t('cancelSubscription.actions.submitEnterprise')}
          </BtnDanger>
        </ButtonRow>
      </>
    );
  };

  const renderStep3 = () => (
    <>
      <StepHeader>
        <StepTitle>
          {t(
            scenario === 'trial'
              ? 'cancelSubscription.step3.titleTrial'
              : scenario === 'enterprise'
              ? 'cancelSubscription.step3.titleEnterprise'
              : 'cancelSubscription.step3.title',
          )}
        </StepTitle>
        <StepDesc>
          {t(
            scenario === 'trial'
              ? 'cancelSubscription.step3.descTrial'
              : scenario === 'enterprise'
              ? 'cancelSubscription.step3.descEnterprise'
              : 'cancelSubscription.step3.desc',
          )}
        </StepDesc>
      </StepHeader>

      {scenario === 'trial' && renderConfirmTrial()}
      {scenario === 'enterprise' && renderConfirmEnterprise()}
      {(scenario === 'pro' || scenario === 'premium') && renderConfirmPaid()}
    </>
  );

  const renderDoneTrial = () => (
    <SuccessWrap>
      <SuccessIconCircle>✓</SuccessIconCircle>
      <SuccessTitle>{t('cancelSubscription.done.trial.title')}</SuccessTitle>
      <SuccessSub>{t('cancelSubscription.done.trial.sub')}</SuccessSub>
      <SuccessCard>
        <h4>{t('cancelSubscription.done.trial.whatHappensTitle')}</h4>
        <ul>
          {['accessEnded', 'noCharge', 'retention30', 'signUpAgain'].map(
            (k) => (
              <li key={k}>
                <span>✓</span>
                {t(`cancelSubscription.done.trial.items.${k}`)}
              </li>
            ),
          )}
        </ul>
      </SuccessCard>
      <SuccessActions>
        <BtnPrimary type="button" onClick={() => navigate(ROUTES.HOME_PAGE)}>
          {t('cancelSubscription.done.actions.home')}
        </BtnPrimary>
      </SuccessActions>
    </SuccessWrap>
  );

  const renderDonePaid = () => (
    <>
      <UndoBanner>
        <div>
          <UndoText>{t('cancelSubscription.done.undo.text')}</UndoText>
          <UndoSub>
            {t('cancelSubscription.done.undo.sub', { date: accessEndDate })}
          </UndoSub>
        </div>
        <UndoBtn type="button" onClick={handleUndoCancellation}>
          {t('cancelSubscription.done.undo.cta')}
        </UndoBtn>
      </UndoBanner>
      <SuccessWrap style={{ paddingTop: 8 }}>
        <SuccessIconCircle>✓</SuccessIconCircle>
        <SuccessTitle>{t('cancelSubscription.done.paid.title')}</SuccessTitle>
        <SuccessSub
          dangerouslySetInnerHTML={{
            __html: t('cancelSubscription.done.paid.sub', {
              plan: planDisplayName,
              date: accessEndDate,
            }),
          }}
        />
        <SuccessCard $green>
          <h4>
            {t('cancelSubscription.done.paid.activeUntil', {
              date: accessEndDate,
            })}
          </h4>
          <ul>
            {['fullAccess', 'communities', 'events', 'storage'].map((k) => (
              <li key={k}>
                <span>✓</span>
                {t(`cancelSubscription.done.paid.activeItems.${k}`)}
              </li>
            ))}
          </ul>
        </SuccessCard>
        <SuccessCard>
          <h4>
            {t('cancelSubscription.done.paid.afterDate', {
              date: accessEndDate,
            })}
          </h4>
          <ul>
            {['noCharges', 'retention30', 'emailSent', 'resubscribe'].map(
              (k) => (
                <li key={k}>
                  <span>✓</span>
                  {t(`cancelSubscription.done.paid.afterItems.${k}`)}
                </li>
              ),
            )}
          </ul>
        </SuccessCard>
        <SuccessActions>
          <BtnPrimary type="button" onClick={goBackToSubscription}>
            {t('cancelSubscription.done.actions.subscription')}
          </BtnPrimary>
          <BtnSecondary
            type="button"
            onClick={() => navigate(ROUTES.HOME_PAGE)}
          >
            {t('cancelSubscription.done.actions.home')}
          </BtnSecondary>
        </SuccessActions>
      </SuccessWrap>
    </>
  );

  const renderDoneEnterprise = () => (
    <SuccessWrap>
      <SuccessIconCircle>📋</SuccessIconCircle>
      <SuccessTitle>
        {t('cancelSubscription.done.enterprise.title')}
      </SuccessTitle>
      <SuccessSub
        dangerouslySetInnerHTML={{
          __html: t('cancelSubscription.done.enterprise.sub', {
            manager:
              enterpriseAccountManagerName ||
              t('cancelSubscription.retention.enterprise.managerFallback'),
          }),
        }}
      />
      <SuccessCard $green>
        <h4>{t('cancelSubscription.done.enterprise.stayActiveTitle')}</h4>
        <ul>
          {['fullAccess', 'manager', 'export'].map((k) => (
            <li key={k}>
              <span>✓</span>
              {t(`cancelSubscription.done.enterprise.stayActiveItems.${k}`)}
            </li>
          ))}
        </ul>
      </SuccessCard>
      <SuccessCard>
        <h4>{t('cancelSubscription.done.enterprise.nextStepsTitle')}</h4>
        <ul>
          {['contact', 'review', 'export', 'email'].map((k) => (
            <li key={k}>
              <span>✓</span>
              {t(`cancelSubscription.done.enterprise.nextStepsItems.${k}`)}
            </li>
          ))}
        </ul>
      </SuccessCard>
      <SuccessActions>
        <BtnPrimary type="button" onClick={goBackToSubscription}>
          {t('cancelSubscription.done.actions.subscription')}
        </BtnPrimary>
        {enterpriseAccountManagerEmail ? (
          <BtnSecondary
            as="a"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore styled `as` widening
            href={`mailto:${enterpriseAccountManagerEmail}`}
          >
            {t('cancelSubscription.done.actions.contactManager')}
          </BtnSecondary>
        ) : null}
      </SuccessActions>
    </SuccessWrap>
  );

  const renderStep4 = () => (
    <>
      {scenario === 'trial' && renderDoneTrial()}
      {scenario === 'enterprise' && renderDoneEnterprise()}
      {(scenario === 'pro' || scenario === 'premium') && renderDonePaid()}
    </>
  );

  const breadcrumbCurrent = useMemo(() => {
    if (scenario === 'trial') return t('cancelSubscription.breadcrumb.trial');
    return t('cancelSubscription.breadcrumb.subscription');
  }, [scenario, t]);

  // Suppress unused warning for cancelledAt - it future-proofs success animation.
  void cancelledAt;

  return (
    <OutsideAppPageContentWrapper minHeight="100svh">
      <PageWrap>
        <Breadcrumb
          items={[
            {
              label: t('cancelSubscription.breadcrumb.billing'),
              to: subscriptionPath,
            },
            { label: breadcrumbCurrent },
          ]}
        />

        {isResolvingInitialStep ? (
          <InitialLoaderWrap aria-live="polite" aria-busy>
            <Loader size="60px" type={null} />
          </InitialLoaderWrap>
        ) : (
          <>
            {renderProgress()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </>
        )}
      </PageWrap>
    </OutsideAppPageContentWrapper>
  );
};

export default CancelSubscriptionPage;
